import React, { useState } from "react";
import _ from "lodash";
import {
  EmployeeOfferDetails,
  NameAndId,
  OfferFilterV2,
  RecurringIntervalUnitV2,
} from "../../../../types/Transaction";
import { Popover, PopoverContent } from "./Popover";
import { HStack, VStack } from "../../../../components/utils";
import { Search } from "./ComponentUtils";
import { Input } from "../../../../components/shared/InputField";
import { MultiCheckBoxPopOver } from "./MultiCheckBoxPopOver";
import { NumberFilterPopOver } from "./NumberFilterPopOver";
import ExcludedPersonnel from "./ExcludedPersonnel";
import { ChvronDown } from "../../../../utils/assets/chevronDown";
import { ChevronUp } from "../../../../utils/assets/chevronUp";
import AddPersonnelFilter from "./AddPersonnel";
import ParticipationConstraintFilter from "./ParticipationConstraintFilter";
import { MultiCheckBoxNameAndIdPopOver } from "./MultiCheckBoxNameAndIdPopOver";

export interface FilterType {
  id: string;
  label: string;
  options?: string[] | { name: string; id: string }[];
  type: string;
}

const FilterOptions = ({
  formik,
  disableUpdate = false,
}: {
  formik: any;
  disableUpdate?: boolean;
}) => {
  const values: EmployeeOfferDetails[] = formik.values.previewList || [];

  const filterOptions: FilterType[] = [
    {
      id: "department",
      label: "Department",
      options: _.uniq(values.map((emp) => emp.employeeDepartment)) || [],
      type: "multiCheckbox",
    },
    { id: "vestedOptions", label: "Vested Options", type: "number" },
    { id: "eligibleOptions", label: "Eligible options", type: "number" },
    {
      id: "employmentType",
      label: "Personnel Type",
      options: ["EMPLOYEE", "CONSULTANT"],
      type: "multiCheckbox",
    },
    {
      id: "tenure",
      label: "Tenure",
      type: "number",
    },
    {
      id: "status",
      label: "Employment Status",
      options: ["EMPLOYED", "RESIGNED"],
      type: "multiCheckbox",
    },
    {
      id: "planIds",
      label: "Plans",
      options:
        _.uniqBy(
          _.flatMap(
            values.map((emp) =>
              emp.grantRelatedDetails.map((g) => ({
                id: g.planId,
                name: g.planName,
              }))
            )
          ),
          (e) => e.id
        ) || [],
      type: "multiCheckboxNameAndId",
    },
    {
      id: "companyIds",
      label: "Companies",
      options: _.uniqBy(
        _.compact([
          ...values.map((emp) => ({
            id: emp.subsidiaryCompanyId,
            name: emp.subsidiaryCompanyName,
          })),
          ...values.map((emp) => ({
            id: emp.companyId,
            name: emp.companyName,
          })),
        ]),
        (e) => e.id
      ),
      type: "multiCheckboxNameAndId",
    },
    {
      id: "excludedPersonnel",
      label: "Excluded Personnel",
      options: _.uniqBy(
        values.flatMap((emp) => ({
          name: emp.employeeName,
          id: emp.id || emp.employeeId,
        })) || [],
        (e) => e.id
      ),
      type: "excludedPersonnel",
    },
    {
      id: "addPersonnel",
      label: "Add Personnel",
      options: _.uniqBy(
        values.flatMap((emp) => ({
          name: emp.employeeName,
          id: emp.id || emp.employeeId,
        })) || [],
        (e) => e.id
      ),
      type: "addPersonnel",
    },
    {
      id: "participationConstraints",
      label: "Participation Filter",
      options: [
        RecurringIntervalUnitV2.DAYS,
        RecurringIntervalUnitV2.MONTH,
        RecurringIntervalUnitV2.QUARTER,
        RecurringIntervalUnitV2.YEAR,
      ],
      type: "participationFilter",
    },
    {
      id: "nationality",
      label: "Nationality",
      options: _.uniq(
        values.map((emp) => emp.nationality).filter(Boolean) as string[]
      ),
      type: "multiCheckbox",
    },
  ];

  const [showAllFilters, setShowAllFilters] = useState(!disableUpdate);

  return (
    <HStack className="gap-4">
      <HStack className="flex-wrap justify-start gap-4">
        {/* Render the first 3 filters */}
        <HStack className="flex-wrap justify-start gap-4">
          {filterOptions.slice(0, 4).map((filter, index) => (
            <HStack key={index}>
              {filter.type === "multiCheckbox" && (
                <MultiCheckBoxPopOver
                  index={index}
                  filter={{
                    ...filter,
                    options: filter.options as string[],
                  }}
                  formik={formik}
                  disableUpdate={disableUpdate}
                />
              )}
              {filter.type === "multiCheckboxNameAndId" && (
                <MultiCheckBoxNameAndIdPopOver
                  index={index}
                  filter={{
                    ...filter,
                    options: filter.options as NameAndId[],
                  }}
                  formik={formik}
                  disableUpdate={disableUpdate}
                />
              )}
              {filter.type === "number" && (
                <NumberFilterPopOver
                  index={index}
                  filter={filter}
                  formik={formik}
                  disableUpdate={disableUpdate}
                />
              )}
              {filter.type === "excludedPersonnel" && (
                <ExcludedPersonnel
                  formik={formik}
                  options={filter.options as { name: string; id: string }[]}
                  disableUpdate={disableUpdate}
                />
              )}
              {filter.type === "addPersonnel" && (
                <AddPersonnelFilter
                  formik={formik}
                  options={filter.options as { name: string; id: string }[]}
                  disableUpdate={disableUpdate}
                />
              )}
              {filter.type === "participationFilter" && (
                <ParticipationConstraintFilter
                  filter={filter}
                  formik={formik}
                  disableUpdate={disableUpdate}
                />
              )}
            </HStack>
          ))}

          {/* Render additional filters if showAllFilters is true */}
          {showAllFilters &&
            filterOptions.slice(4).map((filter, index) => (
              <HStack key={index + 4}>
                {filter.type === "multiCheckbox" && (
                  <MultiCheckBoxPopOver
                    index={index + 4}
                    filter={{
                      ...filter,
                      options: filter.options as string[],
                    }}
                    formik={formik}
                    disableUpdate={disableUpdate}
                  />
                )}
                {filter.type === "multiCheckboxNameAndId" && (
                  <MultiCheckBoxNameAndIdPopOver
                    index={index}
                    filter={{
                      ...filter,
                      options: filter.options as NameAndId[],
                    }}
                    formik={formik}
                    disableUpdate={disableUpdate}
                  />
                )}
                {filter.type === "number" && (
                  <NumberFilterPopOver
                    index={index + 4}
                    filter={filter}
                    formik={formik}
                    disableUpdate={disableUpdate}
                  />
                )}
                {filter.type === "excludedPersonnel" && (
                  <ExcludedPersonnel
                    formik={formik}
                    options={filter.options as { name: string; id: string }[]}
                    disableUpdate={disableUpdate}
                  />
                )}
                {filter.type === "addPersonnel" && (
                  <AddPersonnelFilter
                    formik={formik}
                    options={filter.options as { name: string; id: string }[]}
                    disableUpdate={disableUpdate}
                  />
                )}
                {filter.type === "participationFilter" && (
                  <ParticipationConstraintFilter
                    filter={filter}
                    formik={formik}
                    disableUpdate={disableUpdate}
                  />
                )}
              </HStack>
            ))}

          {/* Toggle button for showing/hiding additional filters */}
          {filterOptions.length > 4 && (
            <div className="flex items-center justify-end gap-2 cursor-pointer">
              <a
                onClick={() => setShowAllFilters((prev) => !prev)}
                className="items-center text-sm font-medium text-center cursor-pointer"
              >
                {!showAllFilters && `+${filterOptions.length - 4} More`}
              </a>
            </div>
          )}
        </HStack>
      </HStack>
      {disableUpdate && (
        <HStack
          onClick={() => setShowAllFilters((prev) => !prev)}
          className="items-center justify-end cursor-pointer"
        >
          {showAllFilters ? <ChevronUp /> : <ChvronDown />}
        </HStack>
      )}
    </HStack>
  );
};

export default FilterOptions;
